import { ref } from 'vue';
import { defineStore } from 'pinia';
import type {
  Notification,
  UserConversationActivityFrame,
  UserServerFrame,
  WsConnectionState,
} from '@talkvibe/shared';

import { router } from '../router/index.ts';
import { show } from '../services/browser-notifications.ts';
import { UserChannelConnection } from '../services/user-channel.ts';
import { translate } from '../i18n/index.ts';
import {
  describeNotification,
  notificationDestination,
  notificationTag,
} from '../utils/notification-presentation.ts';
import { useAuthStore } from './auth.ts';
import { useCallStore } from './call.ts';
import { useChatStore } from './chat.ts';
import { useNotificationStore } from './notification.ts';
import { usePresenceStore } from './presence.ts';

/**
 * Owns the one socket that stays open for the whole session, and routes what
 * arrives on it to whichever store cares.
 *
 * The connection lives here rather than in any of those stores because it
 * belongs to none of them: presence, notifications and unread counts all
 * arrive on it. Putting it in one of them would make the other two depend on
 * that one being alive.
 *
 * It is also where a system notification is raised, for the same reason: both
 * frames that are worth one arrive here, and `show` decides on its own whether
 * to stay quiet (see `services/browser-notifications.ts`). Nothing below
 * checks a preference — the server already applies the account's notification
 * settings before it sends anything, so a frame arriving means it was wanted.
 */
export const useRealtimeStore = defineStore('realtime', () => {
  const connectionState = ref<WsConnectionState>('disconnected');
  let channel: UserChannelConnection | null = null;

  function dispatch(frame: UserServerFrame): void {
    switch (frame.type) {
      case 'ready':
        // The badge is authoritative from the moment the socket opens, so a
        // reconnect after a long sleep corrects it without a request.
        useNotificationStore().setUnreadCount(frame.payload.unreadNotifications);
        return;
      case 'presence':
        usePresenceStore().apply(frame.payload.states);
        return;
      case 'notification':
        useNotificationStore().receive(frame.payload.notification, frame.payload.unreadCount);
        announce(frame.payload.notification);
        return;
      case 'conversation.activity':
        useChatStore().receiveActivity(frame.payload);
        announceGroupMessage(frame.payload);
        return;
      /**
       * Somebody is calling.
       *
       * This frame carries no SDP — the offer travels on the conversation
       * socket, which is not open on this side yet. All that happens here is
       * the panel going up; the call store opens that socket when the call is
       * accepted and collects the offer the caller is repeating.
       *
       * **A device with no socket cannot be rung.** That is the honest limit of
       * calling without push: a browser with no tab open, or a phone with the
       * app closed, never receives this. The web rings while TalkVibe is open
       * and the desktop app rings with its window closed, because it holds this
       * socket in its main process.
       */
      case 'call.ring':
        useCallStore().incoming(frame.payload);
        return;
      /**
       * The caller gave up, or hung up before it was answered.
       *
       * It arrives here as well as on the conversation socket because of the
       * same gap: a ringing callee has no conversation socket open, so that is
       * the only copy they would get — and without it the panel rings on for a
       * call that no longer exists.
       */
      case 'call.end':
        useCallStore().remoteEnded(frame.payload);
        return;
      case 'error':
        console.warn('user channel error', frame.payload.code, frame.payload.message);
        return;
      default:
        // A frame this client does not know yet: a newer server, mid-deploy.
        return;
    }
  }

  /** The system popup for a notification, worded exactly as its row is. */
  function announce(notification: Notification): void {
    show({
      title: describeNotification(notification),
      tag: notificationTag(notification),
      onOpen: () =>
        void router.push(notificationDestination(notification, useAuthStore().user?.username)),
    });
  }

  /**
   * The system popup for a message in a group.
   *
   * A 1:1 message is not handled here. The server creates a `new_message`
   * notification for one, which arrives on the frame above carrying the
   * sender's name — announcing it from here as well would be two popups about
   * one message, the second of them less informative than the first.
   *
   * A group message creates no notification at all, deliberately: fifty people
   * chatting would bury every other notification a member has. So this is the
   * only signal a group message has, collapsed per conversation so that a
   * burst replaces itself rather than stacking.
   *
   * Telling a group from a 1:1 needs the conversation list, which is loaded by
   * the inbox rather than on sign-in. Until it is, this says nothing: silence
   * is the safe side of that guess, because the other side is a second popup
   * about a message already announced by name.
   */
  function announceGroupMessage(payload: UserConversationActivityFrame['payload']): void {
    const conversation = useChatStore().conversations.find(
      (entry) => entry.id === payload.conversationId,
    );
    if (conversation?.type !== 'group') return;
    show({
      title: translate('notifications.groupMessage', {
        name: conversation.name ?? translate('notifications.aGroup'),
      }),
      tag: `conversation:${payload.conversationId}`,
      onOpen: () => void router.push(`/messages/${payload.conversationId}`),
    });
  }

  function start(): void {
    if (channel) return;
    channel = new UserChannelConnection({
      onFrame: dispatch,
      onStateChange: (state) => {
        connectionState.value = state;
      },
    });
    void channel.connect();
  }

  function stop(): void {
    channel?.disconnect();
    channel = null;
    connectionState.value = 'disconnected';
  }

  /**
   * Subscribes to presence for these people.
   *
   * Safe to call before the socket is up: the connection replays its watch set
   * on every reconnect, and the REST prime covers the gap in the meantime.
   */
  function watch(userIds: string[]): void {
    const ids = userIds.filter((id) => id.length > 0);
    if (ids.length === 0) return;
    void usePresenceStore().prime(ids);
    channel?.watch(ids);
  }

  return { connectionState, start, stop, watch };
});
